import type { AdminOrganizationRow } from '../../../domain/superadmin/types';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { Button } from '../../../components/ui';
import { cn } from '@/lib/utils';
import { OrganizationStatusBadge } from './OrganizationStatusBadge';

const formatLabel = (value: string | null) => {
    if (!value) return null;
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export function OrgIdentity({ row }: { row: AdminOrganizationRow }) {
    return (
        <div className="min-w-0">
            <p className="truncate text-[13px] font-medium text-default">{row.name}</p>
            <p className="truncate text-[11.5px] text-muted">{row.ownerEmail ?? '—'}</p>
        </div>
    );
}

export function PlanCell({ row }: { row: AdminOrganizationRow }) {
    const plan = formatLabel(row.plan);
    const subscription = formatLabel(row.subscriptionStatus);
    return (
        <div className="min-w-0">
            <p className="text-[13px] font-medium text-default">{plan ?? '—'}</p>
            {subscription && (
                <p className="truncate text-[11px] text-muted">{subscription}</p>
            )}
        </div>
    );
}

export function WhatsAppCell({ connected, max }: { connected: number; max: number }) {
    const limit = max === -1 ? '∞' : max;
    return (
        <span
            className={cn(
                'font-mono text-[13px] tabular-nums',
                connected > 0 ? 'font-medium text-success-text' : 'text-muted',
            )}
            title={`${connected} conectado(s) de ${max === -1 ? 'ilimitado' : max} do plano`}
        >
            {connected}/{limit}
        </span>
    );
}

export function ImpersonateButton({
    row,
    onImpersonate,
}: {
    row: AdminOrganizationRow;
    onImpersonate: (org: AdminOrganizationRow) => void;
}) {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2.5 text-[12px]"
            onClick={(e) => {
                e.stopPropagation();
                onImpersonate(row);
            }}
        >
            Entrar como
        </Button>
    );
}

export function OrganizationCard({
    row,
    onOpen,
    onImpersonate,
}: {
    row: AdminOrganizationRow;
    onOpen: (orgId: string) => void;
    onImpersonate?: (org: AdminOrganizationRow) => void;
}) {
    return (
        <article
            role="button"
            tabIndex={0}
            onClick={() => onOpen(row.id)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(row.id);
                }
            }}
            className="cursor-pointer rounded-xl border border-subtle bg-card p-s4 shadow-sm transition-colors hover:bg-surface-subtle"
        >
            <div className="flex items-start justify-between gap-s3">
                <OrgIdentity row={row} />
                <OrganizationStatusBadge row={row} />
            </div>
            <dl className="mt-s3 grid grid-cols-2 gap-x-s4 gap-y-s2 text-[12px]">
                <div>
                    <dt className="text-muted">Plano</dt>
                    <dd className="mt-0.5 text-default">{formatLabel(row.plan) ?? '—'}</dd>
                </div>
                <div>
                    <dt className="text-muted">MRR</dt>
                    <dd className="mt-0.5 font-mono tabular-nums text-default">
                        {formatCurrency(row.mrrCents / 100)}
                    </dd>
                </div>
                <div>
                    <dt className="text-muted">Equipe</dt>
                    <dd className="mt-0.5 font-mono tabular-nums text-default">
                        {row.memberCount}
                    </dd>
                </div>
                <div>
                    <dt className="text-muted">WhatsApp</dt>
                    <dd className="mt-0.5">
                        <WhatsAppCell
                            connected={row.whatsappConnected}
                            max={row.whatsappMax}
                        />
                    </dd>
                </div>
                <div className="col-span-2">
                    <dt className="text-muted">Cadastro</dt>
                    <dd className="mt-0.5 font-mono text-[11.5px] tabular-nums text-muted">
                        {formatDate(row.createdAt)}
                    </dd>
                </div>
            </dl>
            {onImpersonate && (
                <div className="mt-s3 flex justify-end border-t border-subtle pt-s3">
                    <ImpersonateButton row={row} onImpersonate={onImpersonate} />
                </div>
            )}
        </article>
    );
}
